import { t } from "@lingui/macro";
import type { CallerInstruction } from "@repo/common";
import { db, functions, httpsCallable } from "@repo/ui";
import { getAuth } from "firebase/auth";
import { collection, onSnapshot, query } from "firebase/firestore";
import { parsePhoneNumber } from "libphonenumber-js";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { i18n } from "../i18n";
import { getContentFieldProps } from "../utils/textDirection";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";

function canonicalNormalizePhoneNumber(phone: string): string | null {
	try {
		const parsed = parsePhoneNumber(phone.trim());
		return parsed.isValid() ? parsed.format("E.164") : null;
	} catch {
		return null;
	}
}

function errorMessage(error: unknown) {
	return error instanceof Error ? error.message : String(error);
}

function formatDate(value: unknown) {
	const maybeTimestamp = value as { toDate?: () => Date } | null;
	const date =
		maybeTimestamp?.toDate?.() ||
		(value ? new Date(value as string | number | Date) : null);
	return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "—";
}

function instructionModeLabel(mode: string) {
	switch (mode) {
		case "one_time":
			return t({ id: "callerInstruction.mode.oneTime", message: "One time" });
		case "persistent":
			return t({
				id: "callerInstruction.mode.persistent",
				message: "Persistent",
			});
		case "valid_until":
			return t({
				id: "callerInstruction.mode.validUntil",
				message: "Valid until",
			});
		default:
			return mode.replace("_", " ");
	}
}

function instructionStatusLabel(status: string) {
	switch (status) {
		case "pending":
			return t({ id: "callerInstruction.status.pending", message: "Pending" });
		case "delivered":
			return t({
				id: "callerInstruction.status.delivered",
				message: "Delivered",
			});
		case "acknowledged":
			return t({
				id: "callerInstruction.status.acknowledged",
				message: "Acknowledged",
			});
		case "completed":
			return t({
				id: "callerInstruction.status.completed",
				message: "Completed",
			});
		case "canceled":
			return t({
				id: "callerInstruction.status.canceled",
				message: "Canceled",
			});
		case "expired":
			return t({ id: "callerInstruction.status.expired", message: "Expired" });
		default:
			return status;
	}
}

export function CallerInstructionsCard({
	companyId,
	phoneNumber,
	language = "en",
	isOutbound = false,
}: {
	companyId: string;
	phoneNumber: string;
	language?: "en" | "he";
	isOutbound?: boolean;
}) {
	const [instructions, setInstructions] = useState<CallerInstruction[]>([]);
	const [canEdit, setCanEdit] = useState(false);
	const [open, setOpen] = useState(false);
	const [instruction, setInstruction] = useState("");
	const [mode, setMode] = useState("one_time");
	const [validTill, setValidTill] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const normalized = canonicalNormalizePhoneNumber(phoneNumber);

	useEffect(() => {
		const auth = getAuth();
		auth.currentUser?.getIdTokenResult().then((result) => {
			const admins = (result.claims.companyAdmin as string[]) || [];
			setCanEdit(
				result.claims.systemAdmin === true || admins.includes(companyId),
			);
		});
	}, [companyId]);

	useEffect(() => {
		if (!normalized) return;
		return onSnapshot(
			query(
				collection(
					db,
					"companies",
					companyId,
					"callers",
					normalized,
					"instructions",
				),
			),
			(snapshot) => {
				setInstructions(
					snapshot.docs
						.map((doc) => ({
							...(doc.data() as CallerInstruction),
							id: doc.id,
						}))
						.sort(
							(a, b) =>
								Number(b.active) - Number(a.active) ||
								(b.priority || 0) - (a.priority || 0),
						),
				);
			},
		);
	}, [companyId, normalized]);

	const createInstruction = async () => {
		if (!instruction.trim()) return;
		setSubmitting(true);
		try {
			await httpsCallable(
				functions,
				"createCallerInstruction",
			)({
				companyId,
				callerId: phoneNumber,
				instruction,
				mode,
				validTill:
					mode === "valid_until" && validTill
						? new Date(validTill).toISOString()
						: null,
			});
			toast.success(
				t({
					id: "callerInstruction.createSuccess",
					message: "Caller instruction created.",
				}),
			);
			setOpen(false);
			setInstruction("");
			setMode("one_time");
			setValidTill("");
		} catch (error) {
			toast.error(
				i18n._({
					id: "callerInstruction.createFailed",
					message: "Failed to create instruction: {error}",
					values: { error: errorMessage(error) },
				}),
			);
		} finally {
			setSubmitting(false);
		}
	};

	const cancelInstruction = async (instructionId: string) => {
		try {
			await httpsCallable(
				functions,
				"cancelCallerInstruction",
			)({ companyId, callerId: phoneNumber, instructionId });
			toast.success(
				t({
					id: "callerInstruction.cancelSuccess",
					message: "Instruction canceled.",
				}),
			);
		} catch (error) {
			toast.error(
				i18n._({
					id: "callerInstruction.cancelFailed",
					message: "Failed to cancel instruction: {error}",
					values: { error: errorMessage(error) },
				}),
			);
		}
	};

	if (!canEdit && instructions.length === 0) return null;

	return (
		<div className="space-y-3 border-t pt-4">
			<div className="flex items-center justify-between">
				<h4 className="text-xs font-medium text-muted-foreground">
					{t({
						id: "callerInstruction.standingInstructions",
						message: "Standing instructions",
					})}
				</h4>
				{canEdit && (
					<Button
						variant="outline"
						size="sm"
						className="h-7 text-xs"
						onClick={() => setOpen(true)}
					>
						+{" "}
						{t({
							id: "callerInstruction.addInstruction",
							message: "Add instruction",
						})}
					</Button>
				)}
			</div>
			{instructions.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					{isOutbound
						? t({
								id: "callerInstruction.emptyCallee",
								message: "No standing instructions for this callee.",
							})
						: t({
								id: "callerInstruction.empty",
								message: "No standing instructions for this caller.",
							})}
				</p>
			) : (
				<div className="space-y-2">
					{instructions.map((item) => (
						<div
							key={item.id}
							className="rounded-md border-[1.5px] border-card-border shadow-card p-3 text-sm"
						>
							<div className="mb-2 flex flex-wrap gap-2">
								<Badge variant={item.active ? "default" : "secondary"}>
									{instructionStatusLabel(item.status)}
								</Badge>
								<Badge variant="outline">
									{instructionModeLabel(item.mode)}
								</Badge>
							</div>
							<p className="whitespace-pre-wrap text-foreground">
								{item.instruction}
							</p>
							<div className="mt-2 text-xs text-muted-foreground">
								{t({
									id: "callerInstruction.delivered",
									message: "Delivered",
								})}
								: {formatDate(item.deliveredAt)} ·{" "}
								{t({
									id: "callerInstruction.acknowledged",
									message: "Acknowledged",
								})}
								: {formatDate(item.acknowledgedAt)} ·{" "}
								{t({
									id: "callerInstruction.completed",
									message: "Completed",
								})}
								: {formatDate(item.completedAt)}
								{item.validTill
									? ` · ${t({
											id: "callerInstruction.validTill",
											message: "Valid till",
										})}: ${formatDate(item.validTill)}`
									: ""}
							</div>
							{canEdit && item.active && (
								<Button
									variant="ghost"
									size="sm"
									className="mt-2 h-7 text-xs text-danger-ink"
									onClick={() => cancelInstruction(item.id!)}
								>
									{t({ id: "cancel", message: "Cancel" })}
								</Button>
							)}
						</div>
					))}
				</div>
			)}
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t({
								id: "callerInstruction.addStandingInstruction",
								message: "Add standing instruction",
							})}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="space-y-2">
							<Label>
								{t({
									id: "callerInstruction.instructionLabel",
									message: "Instruction",
								})}
							</Label>
							<Textarea
								value={instruction}
								onChange={(event) => setInstruction(event.target.value)}
								maxLength={1000}
								placeholder={t({
									id: "callerInstruction.instructionPlaceholder",
									message:
										"Next time Mark calls, tell him the trip is postponed.",
								})}
								{...getContentFieldProps(
									instruction,
									language === "he" ? "rtl" : "ltr",
								)}
							/>
						</div>
						<div className="space-y-2">
							<Label>
								{t({ id: "callerInstruction.modeLabel", message: "Mode" })}
							</Label>
							<Select value={mode} onValueChange={setMode}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="one_time">
										{instructionModeLabel("one_time")}
									</SelectItem>
									<SelectItem value="persistent">
										{instructionModeLabel("persistent")}
									</SelectItem>
									<SelectItem value="valid_until">
										{instructionModeLabel("valid_until")}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{mode === "valid_until" && (
							<div className="space-y-2">
								<Label>
									{t({
										id: "callerInstruction.validTill",
										message: "Valid till",
									})}
								</Label>
								<Input
									type="datetime-local"
									dir="ltr"
									className="text-left"
									value={validTill}
									onChange={(event) => setValidTill(event.target.value)}
								/>
							</div>
						)}
					</div>
					<DialogFooter>
						<Button
							onClick={createInstruction}
							disabled={submitting || !instruction.trim()}
						>
							{submitting
								? t({
										id: "callerInstruction.creating",
										message: "Creating...",
									})
								: t({ id: "create", message: "Create" })}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
